import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { androidLightTokens } from "../styles/androidJigsawTokens";

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';
const ACCENT = androidLightTokens.accent;
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

function formatLongDate(date) {
    return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function localDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function buildMonthCells(year, month) {
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
        cells.push(new Date(year, month, day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

function CalendarIcon({ color }) {
    return (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect x="3" y="5" width="18" height="16" rx="3" stroke={color} strokeWidth={2} />
            <path d="M16 3v4M8 3v4M3 10h18" stroke={color} strokeWidth={2} strokeLinecap="round" />
        </svg>
    );
}

function PlayCaret() {
    return (
        <svg width={12} height={12} viewBox="0 0 14 14" aria-hidden>
            <path d="M4 2.2L12.2 7 4 11.8V2.2Z" fill="#ffffff" />
        </svg>
    );
}

/**
 * App Store Daily shot — matches android DailyPuzzlesScreen with calendar open.
 */
export default function AppleDailyMenu({ title, imageUrl, free = true }) {
    const today = useMemo(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }, []);
    const dateLabel = formatLongDate(today);
    const todayKey = localDateKey(today);
    const cells = useMemo(
        () => buildMonthCells(today.getFullYear(), today.getMonth()),
        [today],
    );

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minHeight: "100%",
                bgcolor: androidLightTokens.homeShellBg,
                overflow: "hidden",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    px: 2,
                    pt: "max(env(safe-area-inset-top, 0px), 12px)",
                    pb: 0.75,
                }}
            >
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT,
                        fontWeight: 800,
                        letterSpacing: "1.1px",
                        fontSize: "0.9375rem",
                        textTransform: "uppercase",
                        color: "#1E293B",
                    }}
                >
                    Daily
                </Typography>
            </Box>

            <Box
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    px: 2,
                    pb: 2,
                    gap: 1.25,
                }}
            >
                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: androidLightTokens.shellText,
                            opacity: 0.9,
                            fontSize: "1.75rem",
                            fontWeight: 300,
                            lineHeight: 1,
                        }}
                        aria-hidden
                    >
                        ‹
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.25,
                                py: 0.75,
                                borderRadius: "12px",
                                bgcolor: "#FFFFFF",
                                border: "1px solid rgba(15, 23, 42, 0.08)",
                                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
                            }}
                        >
                            <Box sx={{ display: "flex", lineHeight: 0 }}>
                                <CalendarIcon color={androidLightTokens.shellText} />
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontWeight: 700,
                                    fontSize: "0.9375rem",
                                    color: androidLightTokens.shellText,
                                    lineHeight: 1.2,
                                }}
                            >
                                {dateLabel}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: androidLightTokens.shellText,
                            opacity: 0.28,
                            fontSize: "1.75rem",
                            fontWeight: 300,
                            lineHeight: 1,
                        }}
                        aria-hidden
                    >
                        ›
                    </Box>
                </Box>

                <Box
                    sx={{
                        flex: "1 1 auto",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "20px",
                        overflow: "hidden",
                        bgcolor: "#FFFFFF",
                        border: "1px solid rgba(226, 232, 240, 0.8)",
                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            flex: "1 1 auto",
                            minHeight: 0,
                            aspectRatio: "1 / 1",
                            maxHeight: "72%",
                        }}
                    >
                        {imageUrl ? (
                            <Box
                                component="img"
                                src={imageUrl}
                                alt=""
                                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                        ) : (
                            <Box sx={{ width: "100%", height: "100%", bgcolor: "rgba(15,23,42,0.06)" }} />
                        )}
                        <Box
                            sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                px: 1,
                                py: 0.35,
                                borderRadius: "999px",
                                bgcolor: "rgba(15,23,42,0.72)",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.7rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.04em",
                                    color: "#fffbeb",
                                    lineHeight: 1.2,
                                }}
                            >
                                Today
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                position: "absolute",
                                right: 8,
                                bottom: 8,
                                px: 1,
                                py: 0.35,
                                borderRadius: "6px",
                                bgcolor: free ? "rgba(22,163,74,0.9)" : "rgba(202,138,4,0.92)",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.65rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.04em",
                                    color: "#fff",
                                    lineHeight: 1.2,
                                }}
                            >
                                {free ? "FREE" : "PREMIUM"}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flexShrink: 0, px: 1.5, pt: 0.75, pb: 0.5 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                lineHeight: 1.2,
                                color: "#1C1917",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {title || "Daily puzzle"}
                        </Typography>
                    </Box>
                </Box>

                {/* PLAY — tight under title, smaller than in-app default for the shot */}
                <Box
                    sx={{
                        flexShrink: 0,
                        mt: 0,
                        height: 40,
                        borderRadius: "20px",
                        background: "linear-gradient(135deg, #F2A07A 0%, #D9643A 42%, #C2410C 100%)",
                        boxShadow: "0 4px 14px rgba(217, 100, 58, 0.32)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontWeight: 800,
                            fontSize: "0.875rem",
                            letterSpacing: "0.1em",
                            color: "#ffffff",
                        }}
                    >
                        PLAY
                    </Typography>
                    <PlayCaret />
                </Box>
            </Box>

            {/* Calendar popup (open for the /08 shot) */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2.5,
                    boxSizing: "border-box",
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        bgcolor: "rgba(15, 23, 42, 0.55)",
                    }}
                />
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        width: "100%",
                        maxWidth: 360,
                        borderRadius: "20px",
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        bgcolor: "#FFFFFF",
                        px: 1.75,
                        pt: 1.75,
                        pb: 1.75,
                        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.28)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "1.5rem",
                                color: androidLightTokens.shellText,
                                opacity: 0.9,
                            }}
                        >
                            ‹
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: "1.05rem",
                                fontWeight: 800,
                                color: androidLightTokens.shellText,
                            }}
                        >
                            {`${MONTHS[today.getMonth()]} ${today.getFullYear()}`}
                        </Typography>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "1.5rem",
                                color: androidLightTokens.shellText,
                                opacity: 0.28,
                            }}
                        >
                            ›
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", mb: 0.75 }}>
                        {WEEKDAYS.map((day) => (
                            <Typography
                                key={day}
                                sx={{
                                    width: "14.2857%",
                                    textAlign: "center",
                                    fontFamily: FONT,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    color: androidLightTokens.shellTextMuted,
                                }}
                            >
                                {day}
                            </Typography>
                        ))}
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                        {cells.map((cellDate, idx) => {
                            if (!cellDate) {
                                return <Box key={`empty-${idx}`} sx={{ width: "14.2857%", aspectRatio: "1 / 1" }} />;
                            }
                            const key = localDateKey(cellDate);
                            const isSelected = key === todayKey;
                            const isFuture = key > todayKey;
                            return (
                                <Box
                                    key={key}
                                    sx={{
                                        width: "14.2857%",
                                        aspectRatio: "1 / 1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "78%",
                                            height: "78%",
                                            borderRadius: "999px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: isSelected ? "#D9643A" : "transparent",
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: FONT,
                                                fontSize: "0.875rem",
                                                fontWeight: 700,
                                                color: isSelected
                                                    ? "#FFFFFF"
                                                    : isFuture
                                                      ? "rgba(15,23,42,0.25)"
                                                      : androidLightTokens.shellText,
                                            }}
                                        >
                                            {cellDate.getDate()}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            mt: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box
                            sx={{
                                minHeight: 40,
                                px: 2,
                                borderRadius: "10px",
                                bgcolor: "#F1F5F9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: "0.875rem",
                                    fontWeight: 800,
                                    color: ACCENT,
                                }}
                            >
                                Today
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                color: androidLightTokens.shellTextMuted,
                                px: 1.5,
                            }}
                        >
                            Close
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
